/**
 * Per-page `<title>` and `<meta>`, set from the component that owns the page.
 *
 * The public pages are prerendered (`scripts/prerender.mjs`) once per language,
 * and the prerender captures whatever these effects have written by the time
 * the page settles — so each frozen HTML file carries its own title,
 * description, canonical and the full set of `hreflang` alternates pointing at
 * its siblings in the other languages. At runtime the same effects keep the
 * tab title and tags in step with client navigation. Pages behind sign-in do
 * not bother; nobody indexes those.
 */
import { useEffect } from "react";
import { absolutizeBreadcrumbs, toJsonLd } from "@/lib/json-ld";
import { currentPath, localize, useUrlLocale } from "@/lib/router";
import { OG_HEIGHT, OG_WIDTH, ogAlt, ogCard, ogImagePath, type OgLocale } from "@/lib/og";

interface Props {
  title: string;
  description: string;
  /**
   * JSON-LD for this page, as plain objects — a `FAQPage` for the two rules
   * pages, a `BreadcrumbList` where a URL has a parent (paths without language
   * prefix; `absolutizeBreadcrumbs` makes them absolute URLs). Written into one
   * `<script>` that this component owns, so a navigation replaces the previous
   * page's markup instead of stacking on top of it.
   */
  schema?: object[];
}

const ALTERNATES: readonly string[] = ["pl", "uk", "ru", "en"];

/** A page with no card of its own (there is none for the app screens) falls back to the icon. */
const FALLBACK_IMAGE = "/icons/icon-512.png";

function upsertMeta(attr: "name" | "property", key: string, value: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** The one JSON-LD block on the page, replaced wholesale on every render. */
function upsertSchema(schema: object[] | undefined, origin: string): void {
  const id = "page-schema";
  const existing = document.getElementById(id);
  if (!schema || schema.length === 0) {
    existing?.remove();
    return;
  }
  const el = existing ?? document.createElement("script");
  if (!existing) {
    el.id = id;
    el.setAttribute("type", "application/ld+json");
    document.head.appendChild(el);
  }
  // A page does not know its origin; it writes `{origin}/...` and this fills it in.
  el.textContent = JSON.stringify(toJsonLd(schema)).replaceAll("{origin}", origin);
}

export function PageMeta({ title, description, schema }: Props) {
  const locale = useUrlLocale();
  useEffect(() => {
    const origin = window.location.origin;
    const route = currentPath();
    const canonical = origin + window.location.pathname;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:locale", locale);
    upsertMeta("property", "og:site_name", "polskiegzamin");
    // The share card is drawn per page and language at build time
    // (`scripts/og/render.ts`); the alt text is what it says, in the page's language.
    const card = ogCard(route, locale as OgLocale);
    const cardPath = ogImagePath(route, locale as OgLocale);
    const image = origin + (cardPath ?? FALLBACK_IMAGE);
    upsertMeta("property", "og:image", image);
    if (card && cardPath) {
      upsertMeta("property", "og:image:width", String(OG_WIDTH));
      upsertMeta("property", "og:image:height", String(OG_HEIGHT));
      upsertMeta("property", "og:image:type", "image/png");
      upsertMeta("property", "og:image:alt", ogAlt(card));
      upsertMeta("name", "twitter:image:alt", ogAlt(card));
    }
    upsertMeta("name", "twitter:card", card ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertLink("canonical", canonical);

    for (const alt of ALTERNATES) upsertLink("alternate", origin + localize(route, alt), alt);
    upsertLink("alternate", origin + localize(route, "pl"), "x-default");
    upsertSchema(schema && absolutizeBreadcrumbs(schema, origin, locale, canonical), origin);
  }, [title, description, locale, schema]);
  return null;
}
